import React, { useEffect, useState } from 'react'
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Typography,
} from '@material-ui/core'

import { useVoiceAgentIntake } from '../../../hooks/useVoiceAgentIntake'
import { useVoiceAgentStyles } from '../useVoiceAgentStyles'
import { usePhoneStyles } from '../../Phone/usePhoneStyles'

export const IntakePanel: React.FC = () => {
    const classes = useVoiceAgentStyles()
    const phone = usePhoneStyles()
    const {
        intakeSettings,
        saveIntake,
        refetch,
        isLoading,
    } = useVoiceAgentIntake()

    const [selectedCardId, setSelectedCardId] = useState<number | ''>('')
    const [selectedFields, setSelectedFields] = useState<string[]>([])
    const [selectedQuestionIds, setSelectedQuestionIds] = useState<number[]>([])
    const [intakeHydrated, setIntakeHydrated] = useState(false)

    useEffect(() => {
        if (!intakeSettings || intakeHydrated) return
        setSelectedCardId(intakeSettings.guest_card_id ?? '')
        const allFieldKeys = intakeSettings.available_fields.map((f) => f.key)
        setSelectedFields(
            intakeSettings.voice_intake_fields.length > 0
                ? intakeSettings.voice_intake_fields
                : allFieldKeys,
        )
        setSelectedQuestionIds(intakeSettings.voice_intake_question_ids)
        setIntakeHydrated(true)
    }, [intakeSettings, intakeHydrated])

    const handleCardChange = async (cardId: number) => {
        setSelectedCardId(cardId)
        setIntakeHydrated(false)
        try {
            await saveIntake.mutateAsync({ guest_card_id: cardId })
            refetch()
        } catch {
            // toast handled by hook
        }
    }

    const toggleField = (key: string) => {
        setSelectedFields((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
        )
    }

    const toggleQuestion = (id: number) => {
        setSelectedQuestionIds((prev) =>
            prev.includes(id) ? prev.filter((q) => q !== id) : [...prev, id],
        )
    }

    const handleSave = async () => {
        if (!selectedCardId) return
        try {
            await saveIntake.mutateAsync({
                guest_card_id: selectedCardId as number,
                voice_intake_fields: selectedFields,
                voice_intake_question_ids: selectedQuestionIds,
            })
        } catch {
            // toast handled by hook
        }
    }

    if (isLoading && !intakeSettings) {
        return <Typography color="textSecondary">Loading intake...</Typography>
    }

    if (!intakeSettings || intakeSettings.guest_cards.length === 0) {
        return (
            <Box className={phone.card} p={3} style={{ maxWidth: 720 }}>
                <Typography className={classes.fieldSectionTitle}>
                    Caller intake
                </Typography>
                <Typography color="textSecondary">
                    No guest cards are available for this property yet.
                </Typography>
            </Box>
        )
    }

    const {
        guest_cards,
        available_fields,
        available_questions,
    } = intakeSettings

    return (
        <Box className={phone.card} p={3} style={{ maxWidth: 720 }}>
            <Typography className={classes.fieldSectionTitle}>
                Caller intake
            </Typography>
            <Typography className={classes.fieldSectionDesc}>
                Choose a guest card and which questions the AI asks callers.
            </Typography>

            <Box className={classes.fieldGroup}>
                <FormControl variant="outlined" size="small" fullWidth>
                    <InputLabel>Guest card</InputLabel>
                    <Select
                        value={selectedCardId}
                        onChange={(e) =>
                            handleCardChange(e.target.value as number)
                        }
                        label="Guest card"
                    >
                        {guest_cards.map((gc) => (
                            <MenuItem key={gc.id} value={gc.id}>
                                {gc.card_name}
                                {gc.default ? ' (default)' : ''}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {available_fields.length > 0 && (
                <>
                    <Typography className={classes.intakeSubLabel}>
                        Standard fields
                    </Typography>
                    {available_fields.map((field) => (
                        <Box key={field.key} className={classes.checklistItem}>
                            <Checkbox
                                checked={selectedFields.includes(field.key)}
                                onChange={() => toggleField(field.key)}
                                color="primary"
                                size="small"
                            />
                            <Typography
                                className={classes.checklistLabel}
                                onClick={() => toggleField(field.key)}
                            >
                                {field.label}
                            </Typography>
                        </Box>
                    ))}
                </>
            )}

            {available_questions.length > 0 && (
                <>
                    <Typography className={classes.intakeSubLabel}>
                        Custom questions
                    </Typography>
                    {available_questions.map((q) => (
                        <Box key={q.id} className={classes.checklistItem}>
                            <Checkbox
                                checked={selectedQuestionIds.includes(q.id)}
                                onChange={() => toggleQuestion(q.id)}
                                color="primary"
                                size="small"
                            />
                            <Typography
                                className={classes.checklistLabel}
                                onClick={() => toggleQuestion(q.id)}
                            >
                                {q.question}
                                <span className={classes.checklistType}>
                                    (
                                    {q.question_type
                                        .toLowerCase()
                                        .replace('_', ' ')}
                                    )
                                </span>
                            </Typography>
                        </Box>
                    ))}
                </>
            )}

            <Box style={{ marginTop: 16 }}>
                <Button
                    variant="contained"
                    color="primary"
                    className={classes.btnPrimary}
                    onClick={handleSave}
                    disabled={saveIntake.isLoading || !selectedCardId}
                >
                    {saveIntake.isLoading ? 'Saving...' : 'Save intake'}
                </Button>
            </Box>
        </Box>
    )
}
